import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Menu, Tooltip } from 'antd';
import {
  CloudDownloadOutlined,
  EditOutlined,
  TagOutlined,
  FolderOpenOutlined,
  BellOutlined,
  MergeCellsOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  ClipboardText,
  FolderSimplePlus,
  ListChecks,
  CaretDown,
} from '@phosphor-icons/react';

import ReferralRowActionsContext from './ReferralRowActionsContext';
import { deriveReferralActionState } from './referralActionState';
import { getArchiveSoftBlockStatuses } from './helpers/archiveSoftBlock';
import { trackReferralActionClicked } from '../../../../global/analytics';
import {
  chartClarityTooltips,
  hasFeatureInLocalStorage,
  INBOX_ACTION_CLARITY_FEATURE,
} from '../../../../global/featureFlags';

// Stop a click/double-click from bubbling to the InboxTable row handlers (which
// would open the viewer). Applied to the trigger wrapper only — NOT to the
// checkbox (originNode), so the checkbox keeps toggling selection normally.
const stopEvent = (event) => {
  if (!event) return;
  if (typeof event.preventDefault === 'function') event.preventDefault();
  if (typeof event.stopPropagation === 'function') event.stopPropagation();
};

// Keyboard variant: stop the event from bubbling to row handlers but do NOT
// preventDefault — otherwise Enter/Space native <button> activation is cancelled
// and keyboard users can't open the quick-actions menu (hover is mouse-only).
const stopKeyPropagation = (event) => {
  if (!event) return;
  if (typeof event.stopPropagation === 'function') event.stopPropagation();
};

// Records a selection-scoped action should operate on, matching the top toolbar:
// the 'all' sentinel expands to every row on the page.
const resolveSelectedRecords = (latest) => {
  const selection = latest.selectedData || [];
  if (selection[0] === 'all') return latest.data || [];
  return selection;
};

// Per-row quick-actions menu that lives INSIDE the AntD selection column (via
// rowSelection.renderCell) next to the checkbox. It reuses the SAME page-level
// useReferralActions object and the SAME gating helper as the top toolbar, so
// behavior can't drift. If the hovered row is already part of the selection the
// menu acts on the WHOLE selection and leaves the selection untouched; otherwise
// it selects just that row on open and acts as a single-row menu.
const RowQuickActions = ({ record, originNode }) => {
  const contextRef = useContext(ReferralRowActionsContext);
  const [open, setOpen] = useState(false);

  const ctx = contextRef ? contextRef.current : null;
  // Always render the checkbox; only add the trigger when wired up under a page.
  if (!ctx || !record) return originNode || null;

  const {
    actions,
    flags = {},
    hasEmrIntegrationCredential = false,
    autoUploadEmr = '',
    allowReplyChartActions = false,
    credentialSupportsStandaloneTask = false,
    useReferralArchiveWarnings = true,
    selectedData = [],
  } = ctx;

  const { autoUploadToEMR, createPatientChart } = flags;

  const isRowInSelection = selectedData[0] === 'all' || selectedData.some((selectedRow) => (
    selectedRow
    && ((record.send_token && selectedRow.send_token === record.send_token)
      || (record.file_token && selectedRow.file_token === record.file_token))
  ));

  // In-selection => operate on the full selection (toolbar parity). Not in
  // selection => single-row menu scoped to this record.
  const effectiveSelectedData = isRowInSelection ? selectedData : [record];

  const {
    selectedCount,
    replySelected,
    viewingArchivedRecords,
    uploadDisabled,
    chartReplyDisabled,
    disableChartActions,
    canEdit,
    canStandaloneTask,
  } = deriveReferralActionState({
    selectedData: effectiveSelectedData,
    flags,
    actions,
    hasEmrIntegrationCredential,
    autoUploadEmr,
    allowReplyChartActions,
    credentialSupportsStandaloneTask,
  });

  const isMultiSelection = selectedCount > 1;
  // Number of records the selection-scoped actions will affect (expands the 'all'
  // sentinel to every page row). Drives the count labels; a single selection
  // keeps plain labels ('1 record' is never shown).
  const effectiveRecords = isRowInSelection ? resolveSelectedRecords(ctx) : [record];
  const affectedCount = effectiveRecords.length;
  const showCounts = affectedCount > 1;
  const withCount = (verb) => (showCounts ? `${verb} ${affectedCount} records` : verb);
  // Checked rows (part of the selection) open on hover with a small delay AND on
  // click. Rows that aren't selected keep the lighter click-to-open behavior.
  // Click stays available everywhere so the menu is reachable by keyboard
  // (focus + Enter/Space) and touch (tap).
  const triggerModes = isRowInSelection ? ['hover', 'click'] : ['click'];

  const clarityTooltips = chartClarityTooltips(autoUploadEmr);
  // ENG-3339: with inbox_action_clarity off, Notify still carries the department
  // chips and this second entry does not exist.
  const actionClarityEnabled = hasFeatureInLocalStorage(INBOX_ACTION_CLARITY_FEATURE);
  const taskLabel = autoUploadEmr === 'Kareo' ? 'Create Note' : 'Create Task';
  const taskLabelAction = autoUploadEmr === 'Kareo' ? 'create_note' : 'create_task';

  const handleVisibleChange = (visible) => {
    setOpen(visible);
    // Only collapse the selection to this row when it isn't already selected;
    // opening the menu on a row that's part of a multi-selection preserves it.
    if (visible && !isRowInSelection) {
      const latest = contextRef.current;
      if (latest && typeof latest.rowSelected === 'function') {
        latest.rowSelected([record]);
      }
    }
  };

  const invoke = (menuInfo, action, run) => {
    stopEvent(menuInfo && menuInfo.domEvent);
    const latest = contextRef.current || {};
    // Read the affected-selection metadata from the live context at click time,
    // not the render closure: opening on an unselected row dispatches
    // rowSelected([record]) async, so the closure can be one render stale.
    const latestSelection = Array.isArray(latest.selectedData) ? latest.selectedData : [];
    trackReferralActionClicked({
      action,
      placement: 'row_quick_actions',
      selectedCount: latestSelection.length,
      selectedAll: latestSelection[0] === 'all',
      isArchivedView: viewingArchivedRecords,
    });
    run(latest);
    setOpen(false);
  };

  const runArchive = (latest) => {
    const records = resolveSelectedRecords(latest);
    const tokens = records.map((r) => r.send_token);
    if (viewingArchivedRecords) {
      latest.actions.unarchiveRecordOpen(tokens);
      return;
    }
    const softBlockStatuses = useReferralArchiveWarnings
      ? getArchiveSoftBlockStatuses(records)
      : [];
    latest.actions.archiveRecordOpen(tokens, softBlockStatuses);
  };

  const chartActionItems = hasEmrIntegrationCredential
    ? (
      <>
        {autoUploadToEMR && (
          <Menu.Item
            key="sendToChartEhr"
            icon={<ClipboardText size={16} weight="fill" />}
            disabled={disableChartActions}
            onClick={(info) => invoke(info, 'send_to_chart', (latest) => latest.actions.handleDropdownSendToChart(record))}
          >
            <Tooltip title={clarityTooltips.sendToChart || null} placement="right">
              <span>Send to Chart</span>
            </Tooltip>
          </Menu.Item>
        )}
        {createPatientChart && (
          <Menu.Item
            key="createChartEhr"
            icon={<FolderSimplePlus size={16} />}
            disabled={disableChartActions}
            onClick={(info) => invoke(info, 'create_new_chart', (latest) => latest.actions.handleDropdownCreateChart(record))}
          >
            Create New Chart
          </Menu.Item>
        )}
        {canStandaloneTask && (
          <Menu.Item
            key="createTaskEhr"
            icon={<ListChecks size={16} />}
            disabled={disableChartActions}
            onClick={(info) => invoke(info, taskLabelAction, (latest) => latest.actions.handleDropdownCreateTask(record))}
          >
            {taskLabel}
          </Menu.Item>
        )}
      </>
    )
    : (
      <>
        {autoUploadToEMR && (
          <Menu.Item
            key="sendToChart"
            icon={<ClipboardText size={16} weight="fill" />}
            disabled={chartReplyDisabled || uploadDisabled}
            onClick={(info) => invoke(info, 'send_to_chart', (latest) => latest.actions.autoUploadRecord(record))}
          >
            <Tooltip title={clarityTooltips.sendToChart || null} placement="right">
              <span>Send to Chart</span>
            </Tooltip>
          </Menu.Item>
        )}
        {createPatientChart && (
          <Menu.Item
            key="createChart"
            icon={<FolderSimplePlus size={16} />}
            disabled={chartReplyDisabled || uploadDisabled}
            onClick={(info) => invoke(info, 'create_chart', (latest) => latest.actions.newChart())}
          >
            Create Chart
          </Menu.Item>
        )}
        {canStandaloneTask && (
          <Menu.Item
            key="createTask"
            icon={<ListChecks size={16} />}
            disabled={replySelected || uploadDisabled}
            onClick={(info) => invoke(info, taskLabelAction, (latest) => latest.actions.createTask())}
          >
            {taskLabel}
          </Menu.Item>
        )}
      </>
    );

  const overlay = (
    <Menu className="rq-menu">
      {showCounts && (
        <Menu.Item key="rq-header" className="rq-menu__header" disabled>
          {`${affectedCount} records selected`}
        </Menu.Item>
      )}
      {showCounts && <Menu.Divider key="rq-header-divider" />}
      <Menu.Item
        key="edit"
        icon={<EditOutlined />}
        disabled={canEdit}
        onClick={(info) => invoke(info, 'edit', (latest) => latest.actions.editPatientOpen())}
      >
        Edit
      </Menu.Item>
      {chartActionItems}
      <Menu.Item
        key="archive"
        icon={<FolderOpenOutlined />}
        onClick={(info) => invoke(info, viewingArchivedRecords ? 'unarchive' : 'archive', runArchive)}
      >
        {withCount(viewingArchivedRecords ? 'Unarchive' : 'Archive')}
      </Menu.Item>
      <Menu.Item
        key="label"
        icon={<TagOutlined />}
        disabled={replySelected}
        onClick={(info) => invoke(info, isMultiSelection ? 'labels' : 'label', (latest) => latest.actions.editTag())}
      >
        {withCount('Label')}
      </Menu.Item>
      <Menu.Item
        key="notify"
        icon={<BellOutlined />}
        disabled={replySelected}
        onClick={(info) => invoke(info, 'notify', (latest) => latest.actions.notifyModal())}
      >
        {withCount('Notify')}
      </Menu.Item>
      {actionClarityEnabled && (
        <Menu.Item
          key="transferToDepartment"
          icon={<SwapOutlined />}
          disabled={replySelected}
          onClick={(info) => invoke(info, 'transfer_to_department', (latest) => latest.actions.transferToDepartmentModal())}
        >
          {withCount('Transfer to department')}
        </Menu.Item>
      )}
      <Menu.Item
        key="download"
        icon={<CloudDownloadOutlined />}
        disabled={replySelected}
        onClick={(info) => invoke(info, 'download', (latest) => latest.actions.downloadRecord())}
      >
        {withCount('Download')}
      </Menu.Item>
      {isMultiSelection && (
        <Menu.Item
          key="merge"
          icon={<MergeCellsOutlined />}
          onClick={(info) => invoke(info, 'merge_records', (latest) => latest.actions.handleMergeRecords(resolveSelectedRecords(latest)))}
        >
          {withCount('Merge')}
        </Menu.Item>
      )}
      {isMultiSelection && (
        <Menu.Item
          key="mergeArchive"
          icon={<MergeCellsOutlined />}
          onClick={(info) => invoke(info, 'merge_and_archive', (latest) => latest.actions.handleMergeAndArchiveRecords(resolveSelectedRecords(latest)))}
        >
          {withCount('Merge & Archive')}
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    // Layout wrapper: checkbox (originNode) + hover trigger, side by side inside
    // the selection column. Only the trigger stops propagation; the checkbox is
    // left untouched so its normal toggle/select behavior is preserved.
    <span className="rq-selection-cell">
      {originNode}
      <span
        className="rq-cell"
        role="presentation"
        onClick={stopEvent}
        onKeyDown={stopKeyPropagation}
        onDoubleClick={stopEvent}
      >
        <Dropdown
          overlay={overlay}
          trigger={triggerModes}
          mouseEnterDelay={0.12}
          placement="bottomLeft"
          visible={open}
          onVisibleChange={handleVisibleChange}
        >
          <button
            type="button"
            className={`rq-trigger${open ? ' rq-open' : ''}`}
            aria-label="Quick actions"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <CaretDown size={12} weight="bold" />
          </button>
        </Dropdown>
      </span>
    </span>
  );
};

RowQuickActions.propTypes = {
  record: PropTypes.object,
  originNode: PropTypes.node,
};

RowQuickActions.defaultProps = {
  record: null,
  originNode: null,
};

export default RowQuickActions;
